import { useState, useEffect, useRef } from 'react';
import Tooltip from '@mui/material/Tooltip';
import '../styles/stepper.css';
import SignatureModalForMobile from './SignatureModalForMobile';
import BackdropModalSignature from './modals/backdrop_modal_signature';

const ImageCaptureForMobile = ({
  onImageChange,
  title = "Capture an Image",
  subtitle = "",
  btnCameraTitle = "Open Camera",
  keyName = "captured-image",
  preLoadedImage,
  useSignaturePad = true,
  preLoadedSignature,
  hasUploadButton = true,
  hasRemoveButton = false,
  info = "Upload",
  signatureError,
  imageError,
}) => {
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [imageName, setImageName] = useState('');
  const [preview, setPreview] = useState(preLoadedImage);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [showOpenCameraButton, setShowOpenCameraButton] = useState(true);
  const [signature, setSignature] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Get available cameras
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then(devices => {
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setCameras(videoDevices);
      })
      .catch(console.error);
  }, []);

  // Handle preloaded image
  useEffect(() => {
    if (preLoadedImage) {
      if (typeof preLoadedImage === "string") {
        setPreview(preLoadedImage);
        setImage(preLoadedImage);
        setImageName("Captured Image");
      } else if (preLoadedImage instanceof File) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
          setImage(preLoadedImage);
          setImageName(preLoadedImage.name || "Captured Image");
        };
        reader.readAsDataURL(preLoadedImage);
      }
    }
  }, [preLoadedImage]);

  // Handle preloaded signature
  useEffect(() => {
    if (preLoadedSignature) {
      setSignature(preLoadedSignature);
    }
  }, [preLoadedSignature]);

  // Prevent scroll on camera open
  useEffect(() => {
    document.body.style.overflow = isCameraOpen ? 'hidden' : '';
  }, [isCameraOpen]);

  const playClickSound = () => {
    const clickSound = new Audio('/iusevolutione/assets/audio/camera_shutter.mp3');
    clickSound.play();
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    setImage(file);
    setImageName(file.name);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
    setIsCameraOpen(false);
    setShowOpenCameraButton(false);
    onImageChange(file, keyName);
  };

  // const openCamera = () => {
  //   setIsCameraOpen(true);
  //   setPreview(null);
  //   setImageName("");
  //   setShowOpenCameraButton(false);

  //   const constraints = {
  //     video: {
  //       deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
  //       facingMode: 'environment',
  //       width: { ideal: 1080 },
  //       height: { ideal: 1080 },
  //     }
  //   };

  //   navigator.mediaDevices.getUserMedia(constraints)
  //     .then(stream => {
  //       if (videoRef.current) {
  //         videoRef.current.srcObject = stream;
  //         videoRef.current.play().catch(console.error);
  //       }
  //     })
  //     .catch(console.error);
  // };

  const openCamera = () => {
  setIsCameraOpen(true);
  setPreview(null);
  setImageName("");
  setShowOpenCameraButton(false);

  // Use default camera if none selected yet
  if (cameras.length > 0) {
    if (!selectedCamera) {
      setSelectedCamera(cameras[0].deviceId);
    }

    // Define mobile-friendly constraints
    const constraints = {
      video: {
        deviceId: selectedCamera
          ? { exact: selectedCamera }
          : { exact: cameras[0].deviceId }, // safer to wrap in { exact: ... }
        width: { ideal: 275 },
        height: { ideal: 275 },
      },
    };

    // Try to get the camera stream
    navigator.mediaDevices.getUserMedia(constraints)
      .then(stream => {
        try {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;

            // Always add muted + playsInline in video tag to avoid issues on mobile
            videoRef.current.play().catch(playError => {
              console.error("Video play failed: ", playError);
            });
          }
        } catch (error) {
          console.error("Error setting video stream: ", error);
        }
      })
      .catch(err => {
        console.error("Error accessing camera: ", err.name, err.message);
        alert(`Camera access error: ${err.name} - ${err.message}`);
      });
  } else {
    console.warn("No cameras found.");
  }
};


  const takePicture = () => {
  setIsAnimating(true);
  setTimeout(() => setIsAnimating(false), 500);
  playClickSound();

  const video = videoRef.current;
  const canvas = canvasRef.current;
  const context = canvas.getContext('2d');

  // Get visible dimensions of video (what the user actually sees)
  const { videoWidth, videoHeight } = video;

  // Match canvas size to video
  canvas.width = videoWidth;
  canvas.height = videoHeight;

  context.drawImage(video, 0, 0, videoWidth, videoHeight);

  // Stop camera
  video.srcObject.getTracks().forEach(track => track.stop());

  const dataUrl = canvas.toDataURL('image/png');
  setPreview(dataUrl);

  fetch(dataUrl)
    .then(res => res.blob())
    .then(blob => {
      const file = new File([blob], `${keyName}.png`, { type: 'image/png' });
      setImage(file);
      setImageName(file.name);
      onImageChange(file, keyName);
    });

  if (fileInputRef.current) fileInputRef.current.value = '';
  setIsCameraOpen(false);
};


  const retakePhoto = () => {
    setPreview(null);
    setImage(null);
    setImageName('');
    setShowOpenCameraButton(true);
    openCamera();
  };

  const removePhoto = () => {
    setPreview(null);
    setImage(null);
    setImageName('');
    onImageChange(null, keyName);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleButtonClick = () => fileInputRef.current.click();

  const handleCameraChange = (e) => setSelectedCamera(e.target.value);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleSaveSignature = (dataURL) => {
    setSignature(dataURL);
    onImageChange(dataURL, `${keyName}Signature`);
  };

  return (
    <div className='position-relative'>
      {/* Hidden File Input */}
      <input
        className="d-none"
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      {/* Fullscreen Camera View */}
      {isCameraOpen && (
              <>
  <video
  ref={videoRef}
  autoPlay
  playsInline
  muted
  style={{
    position: 'fixed',
    top: 0,
    left: 0,
    display:'block',
    width: '100vw',
    height: '100vh',
    objectFit: 'cover',     // fill screen, crop sides
    transform: 'scaleX(-1)', // mirror
    zIndex: 1000,
  }}
></video>


  <canvas
    ref={canvasRef}
    style={{
      display: 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 1001,
    }}
  />

    {/* Close Button */}
  <button type="button" className='btn btn-danger rounded-circle' style={{
      position: 'fixed',
      top: 20,
      right: 20,
      zIndex: 1100,
      width: 40,
      height: 40,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      cursor: 'pointer',
  }} onClick={() => setIsCameraOpen(false)}>
      <i className="bx bx-x fs-1"></i>
  </button>


 

  {/* Capture Button */}
  <div className='position-absolute w-100 bottom-0 pb-2 d-flex justify-content-center ' style={{zIndex: 1100}}>
 <button
    onClick={takePicture}
    className="btn btn-light rounded-circle"
    style={{
      position: 'fixed',
      bottom: '5%',
      left: 'calc(50% - 20px)',
      zIndex: 1100,
      width: 50,
      height: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
    }}
    aria-label="Capture Photo">
    <i className="bx bx-camera fs-3"></i>
  </button>
  </div>
 



  {/* Switch Camera Button */}
  {/* <button
    onClick={() => {
      if (cameras.length < 2) return; // No switch if only one camera
      const currentIndex = cameras.findIndex(cam => cam.deviceId === selectedCamera);
      const nextIndex = (currentIndex + 1) % cameras.length;
      setSelectedCamera(cameras[nextIndex].deviceId);
    }}
    className="btn btn-secondary rounded-circle"
    style={{
      position: 'fixed',
      bottom: '5%',
      right: 20,
      zIndex: 1100,
      width: 50,
      height: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      cursor: cameras.length > 1 ? 'pointer' : 'not-allowed',
      opacity: cameras.length > 1 ? 1 : 0.5,
    }}
    aria-label="Switch Camera"
    disabled={cameras.length < 2}
  >
    <i className="bx bx-refresh fs-4"></i>
  </button> */}
</>

      )}

      {/* Preview Image */}
      {preview && (
        <div className={`card-img bg-label-info mb-1 position-relative justify-content-center ${isAnimating ? 'camera-blinking' : ''}`}>
          <div className="card w-100">
            <span className="card-title position-absolute mt-2 ms-2 text-white bg-dark p-1 rounded">Captured Image</span>
            <img
              className="card-img-top rounded-top w-100 bg-dark"
              src={preview}
              alt="Preview"
              style={{
                minWidth: "33%",
                height: "auto",
                objectFit: "cover",
                transform: 'scaleX(-1)'
              }}
            />
          </div>
        </div>
      )}

      {/* Upload Placeholder */}
      {!preview && !isCameraOpen && (
        <>
          <div className={`card-img rounded-3 mb-2 d-flex flex-column align-items-center justify-content-center border border-2 w-100 border-dashed ${imageError && 'border-danger border-dashed blinking'}`} style={{ height: 275 }}>
            <h5 className='w-100 text-center'>{info}</h5>
            <i className='bx fs-1 bxs-image'></i>
          </div>

            {imageError && <div className="invalid-feedback d-block blink">{imageError}</div>}

        </>
      )}

      {/* Buttons */}
      <div className="btn-group w-100 gap-1 mt-2" role="group">
        {hasUploadButton && (
          <button type="button" className="btn btn-primary" onClick={handleButtonClick}>Upload</button>
        )}
        {!isCameraOpen && !preview && (
          <button type="button" className="btn btn-primary" onClick={openCamera}>{btnCameraTitle}</button>
        )}
        {preview && (
          <>
            <button type="button" className="btn btn-info" onClick={retakePhoto}>Retake</button>
            {hasRemoveButton && (
              <button type="button" className="btn btn-danger" onClick={removePhoto}>Remove</button>
            )}
          </>
        )}
      </div>

      {/* Signature Section */}
      {useSignaturePad && (
        <div className='mt-3'>
          {!signature && (
            <div className={`card-img rounded-3 my-2 d-flex flex-column align-items-center justify-content-center border w-100 ${signatureError && 'border-danger blinking'}`} style={{ height: 100 }}>
            {signatureError}
            </div>
          )}

          

          <div className="card-body p-0 pb-2 mt-2 position-relative">
            {signature && (
              <img
                className='border rounded-3 w-100 mb-2'
                src={signature}
                alt="Signature"
                height={65}
              />
            )}
            <BackdropModalSignature
              size="xl"
              open={isModalOpen}
              handleClose={closeModal}
              modalTitle="Signature"
              hasCloseButtonBelow={false}
              fullScreen={true}
            >
              <SignatureModalForMobile
                isOpen={isModalOpen}
                onClose={closeModal}
                onSaveSignature={handleSaveSignature}
              />
            </BackdropModalSignature>

            

            <div className='d-flex flex-row align-items-center'>
              <h5 className="text-white rounded bg-label-primary p-2 mb-0">Signature</h5>
              <Tooltip title="Click to Sign" slotProps={{ tooltip: { sx: { fontSize: '1.15em' } } }}>
                <button
                  className="btn btn-label-primary ms-2"
                  type="button"
                  onClick={openModal}
                  aria-label="Open Signature Pad"
                >
                  <i className='bx bxs-edit-alt'></i> Tap to sign
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCaptureForMobile;
